import { Icon } from '@cloudscape-design/components';

export type CalendarPeriodOption = {
  id: string;
  text: string;
  disabled?: boolean;
  disabledReason?: string;
};

export const CALENDAR_PERIOD_OPTIONS: readonly CalendarPeriodOption[] = [
  { text: '30 Days', id: '30-days' },
  { text: '60 Days', id: '60-days' },
  { text: '90 Days', id: '90-days' },
  { text: '180 Days', id: '180-days' },
];

type CalendarPeriodControlProps = {
  selectedId: string;
  onChange: (selectedId: string) => void;
  options?: readonly CalendarPeriodOption[];
  label?: string;
};

export function CalendarPeriodControl({
  selectedId,
  onChange,
  options = CALENDAR_PERIOD_OPTIONS,
  label = 'View past',
}: CalendarPeriodControlProps) {
  return (
    <div className="min-w-0">
      <div className="mb-2 text-sm font-bold text-slate-900">{label}</div>
      <div className="grid grid-cols-4 gap-1.5 sm:gap-2" role="group" aria-label={label}>
        {options.map((option) => {
          const selected = option.id === selectedId;
          const accessibleLabel = option.disabledReason
            ? `${option.text}, ${option.disabledReason}`
            : option.text;

          return (
            <button
              key={option.id}
              type="button"
              aria-label={accessibleLabel}
              aria-pressed={selected}
              disabled={option.disabled}
              title={option.disabledReason}
              onClick={() => onChange(option.id)}
              className={`flex min-h-16 min-w-0 flex-col items-center justify-center gap-1 rounded-md border px-1.5 py-2 text-center font-bold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0972d3] disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 ${
                selected
                  ? 'border-[#0972d3] bg-[#0972d3] text-white hover:border-[#033160] hover:bg-[#033160]'
                  : 'border-slate-400 bg-white text-sky-950 hover:border-[#0972d3] hover:bg-sky-50'
              }`}
            >
              <span aria-hidden="true" className="text-lg leading-none">
                <Icon name="calendar" size="medium" />
              </span>
              <span className="whitespace-nowrap text-xs leading-tight sm:text-sm">
                {option.text}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
